import { resolveApiPath } from "@/lib/utils.ts";
import {
  type ImageSrcOverrides,
  messageImagePath,
  resolveMessageImageSrc,
} from "@/shared/messageImageSrc.ts";

/**
 * Width the resizing endpoints render at inside a card body — roughly 2x the
 * widest a document column shows, so it stays sharp on a retina screen.
 */
const EDITOR_IMAGE_WIDTH = 800;

/**
 * MDXEditor's image preview handler: turn an internal image token into the
 * URL it is served from, so a document body shows the same picture the chat
 * does. Only the displayed source changes — the markdown keeps the token, which
 * is what lets the route move without rewriting saved documents.
 *
 * A source that addresses nothing resolves to an empty string, which the editor
 * paints as its own broken-image placeholder. That beats passing the token
 * through: `<img src="/attachments/…">` would request the SPA origin.
 *
 * `imageSrcs` is a shared reader's view: the paths the server wrote for the
 * images this document shows.
 */
export const resolveEditorImageSrc = async (
  src: string,
  imageSrcs?: ImageSrcOverrides,
): Promise<string> => {
  const source = resolveMessageImageSrc(src);
  if (source.kind === "external") return source.url;
  const path = messageImagePath(source, EDITOR_IMAGE_WIDTH, imageSrcs);
  return path === null ? "" : resolveApiPath(path);
};
